import { ApiError } from '../types';
import { AI_API_BASE_URL } from './endpoints';
import { clearAuthForUnauthorized, getRequestTokens } from './client';

const jsonHeaders = { 'Content-Type': 'application/json' } as const;

async function getAuthHeaders(): Promise<HeadersInit> {
  const tokens = await getRequestTokens();
  const headers: HeadersInit = { ...jsonHeaders };
  if (tokens?.idToken) {
    headers.Authorization = tokens.idToken;
  }
  return headers;
}

async function readError(response: Response, prefix: string): Promise<never> {
  if (response.status === 401) {
    clearAuthForUnauthorized();
  }
  const text = await response.text();
  const message = text || `${prefix}: ${response.status}`;
  throw new ApiError(message, response.status);
}

// Polling (refetchInterval) issues repeated GETs to the same URL — without
// explicit no-store directives, the browser can serve a stale cached
// response instead of re-hitting the network, making polled status fields
// (e.g. "processing") appear stuck until a full page reload forces a fresh
// fetch. Match client.ts's cache-busting so polling actually observes
// status transitions.
const NO_CACHE_HEADERS = {
  'Cache-Control': 'no-store, no-cache, max-age=0, must-revalidate',
} as const;

async function request<T>(path: string, init: RequestInit, errorPrefix: string): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${AI_API_BASE_URL}${path}`, {
    headers: { ...headers, ...NO_CACHE_HEADERS },
    cache: 'no-store',
    ...init,
  });
  if (!response.ok) return readError(response, errorPrefix);
  return response.json() as Promise<T>;
}

async function requestVoid(path: string, init: RequestInit, errorPrefix: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${AI_API_BASE_URL}${path}`, {
    headers: { ...headers, ...NO_CACHE_HEADERS },
    cache: 'no-store',
    ...init,
  });
  if (!response.ok) return readError(response, errorPrefix);
}

export async function aiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) }, 'AI API error');
}

export async function aiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }, 'AI API error');
}

export async function aiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' }, 'AI API error');
}

export async function aiDelete(path: string): Promise<void> {
  await requestVoid(path, { method: 'DELETE' }, 'AI API error');
}

export async function aiPostAbsolute<T>(url: string, body: unknown): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) return readError(response, 'AI API error');
  return response.json() as Promise<T>;
}

/**
 * Opens an SSE stream to the AI chat endpoint.
 * Returns an AsyncGenerator that yields AIStreamChunk objects.
 * The caller should iterate with `for await (const chunk of stream)`.
 */
export async function* aiStream(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): AsyncGenerator<{ id: string; content: string; done: boolean }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${AI_API_BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) return readError(response, 'AI stream error');

  const reader = response.body?.getReader();
  if (!reader) throw new Error('Response body is not readable');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('data: ')) {
        const jsonStr = trimmed.slice(6);
        if (jsonStr === '[DONE]') return;
        try {
          const parsed = JSON.parse(jsonStr) as { id: string; content: string; done: boolean };
          yield parsed;
          if (parsed.done) return;
        } catch {
          continue;
        }
      }
    }
  }

  await reader.cancel();
}
